import { mergeAttributes, Node } from '@tiptap/core';
import { NodeView } from '@tiptap/pm/view';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { TextSelection } from 'prosemirror-state';

// --- Module Augmentation for Custom Command ---
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    draggableBlock: {
      addDraggableBlockAfter: () => ReturnType;
    };
  }
}

// Helper function to create the Plain JS Node View logic
const createDraggableNodeView = () => {
  return (props): NodeView => {
    const { editor, node, getPos, HTMLAttributes } = props;

    // Create the outer DOM element for the Node View
    const dom = document.createElement('div');
    dom.classList.add('draggable-block'); // Add a class for potential styling/JS hooks
    // Ensure attribute values are strings
    Object.entries(HTMLAttributes).forEach(([key, value]) => {
      // Only set attribute if value is not null/undefined and convert to string
      if (value !== null && value !== undefined) {
        dom.setAttribute(key, String(value));
      }
    });

    const dragHandle = document.createElement('div');
    dragHandle.classList.add('drag-handle');
    dragHandle.setAttribute('data-drag-handle', '');
    dragHandle.contentEditable = 'false';
    dragHandle.innerHTML = '&#x2630;'; // Example icon: hamburger menu

    // Create the inner element where ProseMirror will render the node's content
    const contentDOM = document.createElement('div');
    contentDOM.classList.add('content-dom'); // Add a class

    // Append drag handle and contentDOM to the main dom element
    dom.appendChild(dragHandle);
    dom.appendChild(contentDOM);

    return {
      dom, // The outer element
      contentDOM, // The element for PM content
      update: (updatedNode: ProseMirrorNode) => {
        // Only redraw if the node type itself changes.
        // For simple content changes within, ProseMirror can handle updating the contentDOM.
        if (updatedNode.type !== node.type) {
          return false; // Node type changed, requires full redraw
        }
        // Optional: Check if attributes changed if the node view depends on them
        // Example: check if updatedNode.attrs are different from node.attrs

        // Otherwise, assume the existing DOM is fine and PM can update contentDOM.
        return true;
      },
      destroy: () => {
        // TODO (if needed): Remove any custom event listeners added
      },
    };
  };
};

// Define the Tiptap Node Extension Factory Function
export const DraggableBlock = () =>
  Node.create<{
    HTMLAttributes: Record<string, any>;
  }>({
    name: 'draggableBlock',
    group: 'block', // Should behave like other block content
    content: 'block', // Single block per draggable container
    draggable: true, // Enable ProseMirror native dragging for this node
    // isolating: true, // Temporarily remove to see if it affects the loop

    addOptions() {
      return {
        HTMLAttributes: {},
      };
    },

    parseHTML() {
      // How to recognize this node when pasting HTML
      return [{ tag: 'div[data-type="draggable-block"]' }];
    },

    renderHTML({ HTMLAttributes }) {
      // How to render this node to HTML
      // The `0` represents the content hole where child nodes are rendered
      return [
        'div',
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { 'data-type': 'draggable-block' }),
        0,
      ];
    },

    // Use the helper to add the Node View
    addNodeView() {
      return createDraggableNodeView();
    },

    // Potential: Add commands to wrap/unwrap content, handle Enter key, etc.
    addCommands() {
      return {
        addDraggableBlockAfter:
          () =>
          ({ editor, state, dispatch }) => {
            const { selection } = state;
            const parent = selection.$head.node(1);
            if (parent?.type.name !== this.name) {
              console.warn('Cannot addDraggableBlockAfter: Not inside a draggableBlock or wrong depth assumption.');
              return false;
            }

            const posAfter = selection.$head.after(1); // Position after node at depth 1

            if (dispatch) {
              // Check if dispatch function is provided
              const newNode = {
                type: this.name, // node name (draggableBlock)
                content: [{ type: 'paragraph' }], // Ensure new block has default content
              };
              // Use transaction directly for insertion
              const tr = state.tr.insert(posAfter, state.schema.nodeFromJSON(newNode));
              // Set selection inside the new paragraph within the new block
              tr.setSelection(TextSelection.near(tr.doc.resolve(posAfter + 2))); // posAfter + 1 (draggableBlock) + 1 (paragraph)
              dispatch(tr.scrollIntoView());
              return true; // Signal that the command was handled
            }
            return false; // Dispatch not available
          },
      };
    },

    addKeyboardShortcuts() {
      return {
        // Prevent Backspace from "lifting" empty list items out of the list,
        // which would violate draggableBlock's content: 'block' constraint
        Backspace: ({ editor }) => {
          const { $head } = editor.state.selection;
          const parent = $head.node($head.depth - 1);

          // Only intercept if at start of empty list item
          if (parent?.type.name !== 'listItem' || $head.parentOffset !== 0 || parent.textContent.trim()) {
            return false;
          }

          // Find containing list and list item depth
          let listDepth = -1,
            listItemDepth = -1;
          for (let d = $head.depth - 1; d > 0; d--) {
            const node = $head.node(d);
            if ((node.type.name === 'bulletList' || node.type.name === 'orderedList') && listDepth === -1) {
              listDepth = d;
              // If only item, just block the backspace
              if (node.childCount === 1) return true;
            }
            if (node.type.name === 'listItem' && listItemDepth === -1) {
              listItemDepth = d;
            }
          }

          // Delete the empty list item if we found it
          if (listItemDepth !== -1) {
            const itemPos = $head.before(listItemDepth);
            const tr = editor.state.tr.delete(itemPos, itemPos + $head.node(listItemDepth).nodeSize);
            tr.setSelection(TextSelection.near(tr.doc.resolve(itemPos - 2)));
            editor.view.dispatch(tr);
            return true;
          }

          return false;
        },

        Enter: ({ editor }) => {
          const {
            selection: { $head, from, to },
            doc,
          } = editor.state;
          const parent = $head.node($head.depth - 1);

          // Handle Enter in list items
          if (parent?.type.name === 'listItem') {
            // Non-empty: use default split
            if (parent.textContent.trim()) {
              return editor.commands.splitListItem('listItem');
            }

            // Empty item: escape to new draggableBlock
            // Find depths
            let blockDepth = -1,
              itemDepth = -1;
            for (let d = $head.depth; d > 0; d--) {
              if ($head.node(d).type.name === this.name) blockDepth = d;
              if ($head.node(d).type.name === 'listItem' && itemDepth === -1) itemDepth = d;
            }
            if (blockDepth === -1 || itemDepth === -1) return false;

            // Delete empty item and insert new block
            const itemPos = $head.before(itemDepth);
            const tr = editor.state.tr.delete(itemPos, itemPos + $head.node(itemDepth).nodeSize);

            // Recalculate position after deletion
            const newState = editor.state.apply(tr);
            let newBlockDepth = -1;
            for (let d = newState.selection.$head.depth; d > 0; d--) {
              if (newState.selection.$head.node(d).type.name === this.name) {
                newBlockDepth = d;
                break;
              }
            }
            if (newBlockDepth === -1) {
              editor.view.dispatch(tr);
              return true;
            }

            const insertPos = newState.selection.$head.after(newBlockDepth);
            tr.insert(
              insertPos,
              editor.state.schema.nodes.draggableBlock.create(null, [editor.state.schema.nodes.paragraph.create()]),
            );
            tr.setSelection(TextSelection.near(tr.doc.resolve(insertPos + 2)));
            editor.view.dispatch(tr);
            return true;
          }

          // Handle default draggable block Enter behavior
          if (parent.type.name !== this.name) {
            return false;
          }

          // Find the end position of the current active node within the draggable block
          let currentActiveNodeTo = -1;

          doc.descendants((node, pos) => {
            if (currentActiveNodeTo !== -1) {
              return false;
            }

            if (node.type.name === this.name) {
              return;
            }

            const [nodeFrom, nodeTo] = [pos, pos + node.nodeSize];

            if (nodeFrom <= from && to <= nodeTo) currentActiveNodeTo = nodeTo;

            return false;
          });

          const content = doc.slice(from, currentActiveNodeTo)?.toJSON().content;

          return editor
            .chain()
            .insertContentAt(
              { from, to: currentActiveNodeTo },
              {
                type: this.name,
                content,
              },
            )
            .focus(from + 4)
            .run();
        },
      };
    },

    // Add storage to define Markdown serialization
    addStorage() {
      return {
        markdown: {
          // How to serialize this node to Markdown
          serialize: (state, node) => {
            // Simply render the content inside this node
            state.renderContent(node);
            // Ensure block separation after rendering content
            state.closeBlock(node);
          },
          // We don't need custom parsing from Markdown, Tiptap handles structure
          // parse: { ... }
        },
      };
    },
  });
